import LocaleService from 'services/loader-handler-services/locale-service';
import StateService from 'services/state-handler-services/state-service';

// StateBindingService
//
// Puts state variables on the page and keeps them there.
//
// This replaces twine's `domVars`, which mapped a DOM element id to a variable
// from outside - `$('#totalExpenseW4Display').html(value)` - and required three
// coordinated declarations for one displayed number: an empty text item with an
// id, a `domVars` line, and a second `$...Display` variable holding the
// formatted string.
//
// The direction is inverted here. Whatever displays a value declares what it
// needs, once, at the point of use:
//
//   - type: text
//     items:
//       - state: totalExpense
//         format: currency
//
// The format belongs to the binding, so the same variable can render as
// currency in one place and as a bare number in another, and the stored value
// stays numeric so it can still be calculated with.
//
// Blocks own their own DOM. This service resolves the binding, hands back the
// display string, and calls the block when the value changes.

const StateBindingService = {
  // Normalize what an author wrote into a binding descriptor, or undefined if
  // this isn't a state binding.
  //
  // Accepts the shorthand (`state: totalExpense`) and the long form
  // (`state: { name: totalExpense, format: currency }`), and tolerates the
  // format living beside `state`, which reads better in YAML for a text item.
  parse: function (config, defaults = {}) {
    if (!config) {
      return undefined;
    }

    if (typeof config === 'string') {
      return {
        name: StateService.normalizeName(config),
        format: defaults.format,
        precision: defaults.precision,
        currency: defaults.currency,
      };
    }

    // `bind: { to: 'some:event' }` is the text block's pre-existing event
    // binding, not a state binding - leave it alone.
    const name = config.state || config.name;
    if (!name) {
      return undefined;
    }

    return {
      name: StateService.normalizeName(name),
      format: config.format !== undefined ? config.format : defaults.format,
      precision: config.precision !== undefined ? config.precision : defaults.precision,
      currency: config.currency !== undefined ? config.currency : defaults.currency,
    };
  },

  // The display string for a binding right now. Resolves through StateService,
  // so a value an earlier page persisted is read on demand and the first render
  // is already correct - no flash of an empty element waiting for a change
  // event that may never come.
  // The display string for a binding, translated the way every other string a
  // learner reads is.
  //
  // Courses ship with locale keys even when they ship in English, so a variable
  // holding a word holds a key. Twine ran every `domVars` value through
  // `LocaleService.t` for this reason, and the `text` block already translates
  // the `description` sitting next to a `state:` item.
  //
  // Nothing else is harmed by it. i18next has no missing-key handler configured
  // here, so an unmatched key comes back as itself: a number, a formatted
  // currency string and a token nothing translates all pass through unchanged.
  // That holds once i18next has initialized; before then `LocaleService.t`
  // answers '' for everything, which is the same window in which twine's
  // `domVars` rendered blank.
  render: function (binding) {
    const text = StateService.getFormatted(binding.name, {
      format: binding.format,
      precision: binding.precision,
      currency: binding.currency,
    });
    return text === '' ? text : LocaleService.t(String(text));
  },

  // Keep a block's bindings up to date.
  //
  // Subscribes through the view so the listener is disposed with the block, and
  // calls `onUpdate(binding, text)` for each binding whose variable changed.
  // One subscription serves all of a block's bindings.
  observe: function (view, bindings, onUpdate) {
    const list = (bindings || []).filter(binding => binding && binding.name);
    if (list.length === 0) {
      return;
    }

    view.subscribeEvent('state:changed', ({ names }) => {
      list.forEach(binding => {
        if (names.includes(binding.name)) {
          onUpdate(binding, this.render(binding));
        }
      });
    });
  },
};

export default StateBindingService;
